const MECAKO_ANALYTICS_EVENT_PREFIX = 'mecako_';
const MECAKO_GA4_MEASUREMENT_ID = 'G-ZNFF5BGCJZ';

const mecakoVariantItems = new Map();
let mecakoInitialSelectionSent = false;

function initMecakoGoogleAnalytics() {
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function () {
      window.dataLayer.push(arguments);
    };

  if (!document.querySelector(`script[data-mecako-ga4="${MECAKO_GA4_MEASUREMENT_ID}"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MECAKO_GA4_MEASUREMENT_ID}`;
    script.dataset.mecakoGa4 = MECAKO_GA4_MEASUREMENT_ID;
    document.head.appendChild(script);
  }

  window.gtag('js', new Date());
  window.gtag('config', MECAKO_GA4_MEASUREMENT_ID);
}

function sendGa4Event(name, payload = {}) {
  if (typeof window.gtag !== 'function') return;
  window.gtag('event', name, {
    transport_type: 'beacon',
    ...payload,
  });
}

function publishMecakoEvent(name, payload = {}) {
  const eventName = `${MECAKO_ANALYTICS_EVENT_PREFIX}${name}`;
  const eventPayload = {
    ...payload,
    page_path: window.location.pathname,
    page_url: window.location.href,
    page_title: document.title,
  };

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...eventPayload,
  });

  sendGa4Event(eventName, eventPayload);

  if (!window.Shopify?.analytics?.publish) return;
  window.Shopify.analytics.publish(eventName, eventPayload);
}

function cleanShopifyId(id = '') {
  return String(id).split('/').pop() || String(id);
}

function buildGaItem(product, variant, quantity = 1) {
  const item = {
    item_id: cleanShopifyId(variant?.id || product?.id || ''),
    item_name: product?.title || product?.product_title || product?.productTitle || '',
    item_variant: variant?.title || '',
    item_category: 'Oversized Tees',
    quantity,
  };

  const price = Number(variant?.price?.amount);
  if (!Number.isNaN(price)) item.price = price;
  if (variant?.price?.currencyCode) item.currency = variant.price.currencyCode;

  return item;
}

function cacheVariantItem(product, variant) {
  if (!variant?.id) return;
  const item = buildGaItem(product, variant);
  mecakoVariantItems.set(cleanShopifyId(variant.id), item);
}

function getProductForm() {
  return document.querySelector('product-form-component[data-product-id]');
}

function getVariantPicker() {
  return document.querySelector('variant-picker[data-product-id], swatches-variant-picker-component[data-product-id]');
}

function getSelectedVariantId() {
  return (
    getProductForm()?.querySelector('input[name="id"]')?.value ||
    getVariantPicker()?.querySelector('[data-variant-id]:checked')?.dataset?.variantId ||
    ''
  );
}

function getCurrentProductTitle() {
  return (
    getVariantPicker()?.dataset.productTitle ||
    document.querySelector('[data-testid="product-title"]')?.textContent?.trim() ||
    document.querySelector('.product-title')?.textContent?.trim() ||
    document.querySelector('h1')?.textContent?.trim() ||
    ''
  );
}

function getSelectedQuantity() {
  const quantityInput = getProductForm()?.querySelector('input[name="quantity"], input[ref="quantityInput"]');
  return Number(quantityInput?.value) || 1;
}

function buildCurrentFallbackItem(quantity = getSelectedQuantity()) {
  const variantId = cleanShopifyId(getSelectedVariantId());
  return {
    ...(mecakoVariantItems.get(variantId) || {
      item_id: variantId || getProductForm()?.dataset.productId || getVariantPicker()?.dataset.productId || '',
      item_name: getCurrentProductTitle(),
      item_variant: getSelectedVariantOptions(getVariantPicker()).map((option) => option.value).join(' / '),
      item_category: 'Oversized Tees',
    }),
    quantity,
  };
}

function publishPageIntent() {
  const path = window.location.pathname;

  if (path === '/') {
    publishMecakoEvent('landing_viewed', { page_type: 'landing' });
  }

  if (path === '/pages/journal') {
    publishMecakoEvent('journal_viewed', { page_type: 'journal' });
  }
}

function publishProductView(event) {
  const product = event.product;
  const variant = product?.selectedVariant;
  if (!product) return;

  cacheVariantItem(product, variant);

  const item = buildGaItem(product, variant);
  sendGa4Event('view_item', {
    currency: variant?.price?.currencyCode || 'INR',
    value: item.price || undefined,
    items: [item],
  });

  publishSelectedProduct({ source: 'initial_product_view', selectedItem: item, selectedOptions: [] });
}

function getSelectedVariantOptions(variantPicker) {
  if (!variantPicker) return [];

  return Array.from(variantPicker.querySelectorAll('input:checked, select option:checked'))
    .map((option) => ({
      name:
        option.closest('fieldset')?.querySelector('legend')?.textContent?.trim() ||
        option.closest('[data-option-name]')?.getAttribute('data-option-name') ||
        option.getAttribute('name') ||
        'Option',
      value: option.value,
    }))
    .filter((option) => option.value);
}

function publishVariantSelection(event) {
  const variantPicker = event.target?.closest?.('variant-picker');
  if (!variantPicker || variantPicker.closest('product-card')) return;

  const selectedOptions = getSelectedVariantOptions(variantPicker);
  const basePayload = {
    product_id: variantPicker.dataset.productId || '',
    product_title: variantPicker.dataset.productTitle || '',
    product_handle: variantPicker.dataset.productHandle || '',
    selected_options: selectedOptions,
  };

  const eventPromise = event.promise;
  if (eventPromise && typeof eventPromise.then === 'function') {
    eventPromise
      .then(({ variant }) => {
        cacheVariantItem(basePayload, variant);
        const selectedItem = buildGaItem(basePayload, variant);
        publishSelectedProduct({
          ...basePayload,
          source: 'variant_change',
          selectedItem,
          selectedOptions,
          variant_id: variant?.id || event.detail?.variantId || '',
          variant_title: variant?.title || selectedOptions.map((option) => option.value).join(' / '),
          available: variant?.availableForSale ?? null,
          price: variant?.price?.amount || '',
          currency: variant?.price?.currencyCode || '',
        });
      })
      .catch(() => {
        publishSelectedProduct({
          ...basePayload,
          source: 'variant_change',
          selectedItem: buildCurrentFallbackItem(),
          selectedOptions,
          variant_id: event.detail?.variantId || '',
          variant_title: selectedOptions.map((option) => option.value).join(' / '),
          available: null,
        });
      });

    return;
  }

  publishSelectedProduct({
    ...basePayload,
    source: 'variant_change',
    selectedItem: buildCurrentFallbackItem(),
    selectedOptions,
    variant_id: event.detail?.variantId || '',
    variant_title: selectedOptions.map((option) => option.value).join(' / '),
    available: null,
  });
}

function publishSelectedProduct(payload = {}) {
  if (payload.source === 'initial_product_view') {
    if (mecakoInitialSelectionSent) return;
    mecakoInitialSelectionSent = true;
  }

  const item = payload.selectedItem || buildCurrentFallbackItem();
  const selectedOptions = payload.selectedOptions || getSelectedVariantOptions(getVariantPicker());
  const variantTitle =
    payload.variant_title || item.item_variant || selectedOptions.map((option) => option.value).join(' / ');

  sendGa4Event('select_item', {
    item_list_name: 'Product page',
    items: [item],
  });

  publishMecakoEvent('product_selected', {
    product_id: payload.product_id || getVariantPicker()?.dataset.productId || getProductForm()?.dataset.productId || '',
    product_title: payload.product_title || item.item_name || getCurrentProductTitle(),
    product_handle: payload.product_handle || getVariantPicker()?.dataset.productHandle || '',
    variant_id: cleanShopifyId(payload.variant_id || item.item_id || getSelectedVariantId()),
    variant_title: variantTitle,
    selected_options: selectedOptions,
    source: payload.source || 'product_page',
    available: payload.available ?? null,
    price: payload.price || item.price || '',
    currency: payload.currency || item.currency || 'INR',
    item_name: item.item_name,
    item_id: item.item_id,
    items: [item],
  });
}

function publishCheckoutIntent(source) {
  const item = buildCurrentFallbackItem();
  const value = (Number(item.price) || 0) * (Number(item.quantity) || 1);

  publishSelectedProduct({ source: `${source}_checkout_intent`, selectedItem: item });
  publishMecakoEvent('checkout_intent', {
    source,
    product_id: getProductForm()?.dataset.productId || getVariantPicker()?.dataset.productId || '',
    variant_id: item.item_id,
    product_title: item.item_name,
    item_name: item.item_name,
    item_id: item.item_id,
    currency: item.currency || 'INR',
    value: value || undefined,
    items: [item],
  });
  sendGa4Event('begin_checkout', {
    currency: item.currency || 'INR',
    value: value || undefined,
    items: [item],
  });
}

function publishAddToCart(event) {
  if (event.action !== 'add') return;

  event.promise
    ?.then(({ cart }) => {
      const items = event.lines.map((line) => {
        const variantId = cleanShopifyId(line.merchandiseId);
        return {
          ...(mecakoVariantItems.get(variantId) || {
            item_id: variantId,
            item_name: getCurrentProductTitle(),
            item_variant: '',
            item_category: 'Oversized Tees',
          }),
          quantity: line.quantity,
        };
      });

      const currency = cart?.cost?.totalAmount?.currencyCode || items.find((item) => item.price)?.currency || 'INR';
      const value = items.reduce((total, item) => total + (Number(item.price) || 0) * (Number(item.quantity) || 1), 0);

      sendGa4Event('add_to_cart', {
        currency,
        value: value || undefined,
        items,
      });
    })
    .catch(() => {});
}

initMecakoGoogleAnalytics();
publishPageIntent();
document.addEventListener('shopify:product:view', publishProductView);
document.addEventListener('shopify:product:select', publishVariantSelection);
document.addEventListener('shopify:cart:lines-update', publishAddToCart);
document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('.shopify-payment-button, shopify-accelerated-checkout, [data-testid*="Checkout"]')) {
      publishCheckoutIntent('buy_now');
      return;
    }

    if (target.closest('button[name="checkout"], input[name="checkout"], a[href*="/checkout"]')) {
      publishCheckoutIntent('cart_checkout');
    }
  },
  { capture: true }
);
